import _get from 'lodash/get'
import { z } from 'zod'

import { Language } from '@nxt-insurance/constants'

import { CALENDLY_STAGING_LINK } from '@flows/configs/shared/mgaLight/constants'
import type { AnyStepConfiguration } from '@flows/core/types/stepTypes'
import { SubFlow } from '@flows/insurance/types/subFlowTypes'

import type { IncomeProtectionStepParams } from './types'

const getCalendlyPrimaryLink = (params: IncomeProtectionStepParams) => {
  if (params.environment === 'production') {
    return params.getCurrentLanguage() === Language.De
      ? 'https://calendly.com/getsafe-leben-gesundheit/leben-gesundheit'
      : 'https://calendly.com/getsafe-leben-gesundheit/life-health'
  }

  return CALENDLY_STAGING_LINK
}

const getCalendlyAlternativeLink = (params: IncomeProtectionStepParams) => {
  if (params.environment === 'production') {
    return params.getCurrentLanguage() === Language.De
      ? 'https://calendly.com/getsafe-leben-gesundheit/leben-gesundheit-2'
      : 'https://calendly.com/getsafe-leben-gesundheit/life-health-2'
  }

  return CALENDLY_STAGING_LINK
}

export const progressLastStep = (params: IncomeProtectionStepParams) => {
  return {
    schema: z.object({}),
    stepContent: [
      {
        type: 'harmonyTitle',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_mgaCommon:steps.progress_last_step.title',
        },
      },
      {
        type: 'cardHighlightsRow',
        highlights: [
          {
            title: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress_last_step.highlight1.title',
            },
            description: {
              type: 'translatableText',
              translationKey:
                'sharedFlows_mgaCommon:steps.progress_last_step.highlight1.description',
            },
            infoContent: {
              title: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_mgaCommon:steps.progress_last_step.highlight1.infoContent.title',
              },
              description: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_mgaCommon:steps.progress_last_step.highlight1.infoContent.description',
              },
              imageSrc:
                'https://cdn.sanity.io/images/p4gom3ch/production/a681ca3fe7b458402c29c07f9643cbe3b2366311-981x441.png',
            },
          },
          {
            title: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress_last_step.highlight2.title',
            },
            description: {
              type: 'translatableText',
              translationKey:
                'sharedFlows_mgaCommon:steps.progress_last_step.highlight2.description',
            },
            infoContent: {
              title: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_mgaCommon:steps.progress_last_step.highlight2.infoContent.title',
              },
              description: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_mgaCommon:steps.progress_last_step.highlight2.infoContent.description',
              },
              imageSrc:
                'https://cdn.sanity.io/images/p4gom3ch/production/b01f19b3148417182809ed8317c7cac5dafdd061-981x441.png',
            },
          },
          {
            title: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress_last_step.highlight3.title',
            },
            description: {
              type: 'translatableText',
              translationKey:
                'sharedFlows_mgaCommon:steps.progress_last_step.highlight3.description',
            },
            infoContent: {
              title: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_mgaCommon:steps.progress_last_step.highlight3.infoContent.title',
              },
              description: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_mgaCommon:steps.progress_last_step.highlight3.infoContent.description',
              },
              imageSrc:
                'https://cdn.sanity.io/images/p4gom3ch/production/2d81eec4658ecf96a76c04ccfaaa625c1c49317b-981x441.png',
            },
          },
        ],
      },
      {
        type: 'stepper',
        steps: [
          {
            status: 'completed',
            title: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress.stepper.item1.title',
            },
            description: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress.stepper.item1.description',
            },
          },
          {
            status: 'active',
            title: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress.stepper.item2.title',
            },
            description: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress.stepper.item2.description',
            },
          },
          {
            status: 'pending',
            title: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress.stepper.item3.title',
            },
            description: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress.stepper.item3.description',
            },
          },
          {
            status: 'pending',
            title: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress.stepper.item4.title',
            },
            description: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress.stepper.item4.description',
            },
          },
        ],
      },
      {
        type: 'calendly',
        title: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:generic.buttons.continue',
        },
        link: getCalendlyPrimaryLink(params),
        condition: {
          type: 'conditions',
          comparisonType: 'anyOf',
          conditions: [
            {
              type: 'value',
              valuePath: params.helperFunctions.getHelperPath('affiliateSource'),
              expectedValue: 'Check24',
            },
            {
              type: 'conditions',
              comparisonType: 'allOf',
              conditions: [
                {
                  type: 'value',
                  valuePath: params.helperFunctions.getParamPath('illness'),
                  expectedValue: 'no',
                },
                {
                  type: 'conditions',
                  comparisonType: 'anyOf',
                  conditions: [
                    {
                      type: 'value',
                      valuePath: params.helperFunctions.getParamPath('employment'),
                      equalityType: 'notEquals',
                      expectedValue: 'student',
                    },
                    {
                      type: 'value',
                      value: params.getCurrentLanguage(),
                      equalityType: 'notEquals',
                      expectedValue: Language.En,
                    },
                  ],
                },
                {
                  type: 'conditions',
                  comparisonType: 'anyOf',
                  conditions: [
                    {
                      type: 'value',
                      valuePath: params.helperFunctions.getParamPath('income'),
                      expectedValue: 'more_than_74k',
                    },
                    {
                      type: 'value',
                      valuePath: params.helperFunctions.getParamPath('income'),
                      expectedValue: '50k_to_74k',
                    },
                    {
                      type: 'value',
                      valuePath: params.helperFunctions.getParamPath('income'),
                      expectedValue: '30k_to_50k',
                    },
                  ],
                },
              ],
            },
          ],
        },
      },
      {
        type: 'calendly',
        title: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:generic.buttons.continue',
        },
        link: getCalendlyAlternativeLink(params),
        condition: {
          type: 'conditions',
          comparisonType: 'allOf',
          conditions: [
            {
              type: 'value',
              valuePath: params.helperFunctions.getHelperPath('affiliateSource'),
              equalityType: 'notEquals',
              expectedValue: 'Check24',
            },
            {
              type: 'conditions',
              comparisonType: 'allOf',
              conditions: [
                {
                  type: 'value',
                  valuePath: params.helperFunctions.getParamPath('illness'),
                  expectedValue: 'no',
                },
                {
                  type: 'conditions',
                  comparisonType: 'anyOf',
                  conditions: [
                    {
                      type: 'value',
                      valuePath: params.helperFunctions.getParamPath('employment'),
                      equalityType: 'notEquals',
                      expectedValue: 'student',
                    },
                    {
                      type: 'value',
                      value: params.getCurrentLanguage(),
                      equalityType: 'notEquals',
                      expectedValue: Language.En,
                    },
                  ],
                },
                {
                  type: 'conditions',
                  comparisonType: 'anyOf',
                  conditions: [
                    {
                      type: 'value',
                      valuePath: params.helperFunctions.getParamPath('income'),
                      expectedValue: 'less_than_30k',
                    },
                  ],
                },
              ],
            },
          ],
        },
      },
    ],
    skipStep: {
      condition: (flowValues) => {
        return (
          params.platform === 'web' ||
          _get(flowValues, params.helperFunctions.getParamPath('illness')) !== 'no'
        )
      },
      targetStepName: 'success',
    },
    onPrepare: {
      type: 'dispatchedHook',
      callbackConfig: {
        name: 'updateAndCompleteConsultationRequest',
        inputs: [],
        outputs: [],
      },
    },
    onSubmit: {
      type: 'dispatchedHook',
      callbackConfig: {
        name: 'updateConsultationRequest',
        inputs: [],
        outputs: [],
      },
    },
    hideBackButton: true,
    hideFooter: true,
    nextStep: 'appointment_details',
    subFlow: SubFlow.Common,
  } as const satisfies AnyStepConfiguration
}
